import type { Schemas } from '@npc/api-client'
import { lookup } from '@npc/copy'

import { StatusDot, type TagTone } from '../../components/tag'

/**
 * 一家服务商的一种能力。
 *
 * ## 这一块存在的全部理由：把「有凭证」和「能用」分开
 *
 * `status` 是**四态，不是两态**。契约里那段理由很具体：
 *
 * > 把中间两态压成 enabled 的代价是，平台会在**要用它的那一刻**才发现传不上去，
 * > 而备份、签证书、发布恰恰是最不希望发现问题的时刻。
 *
 * 真实样本就是 Cloudflare 的 R2：那对密钥格式全对、既没撤销也没过期，
 * **但账户根本没开通 R2，它从来就没能用过**。两态模型会把它记成「能用」，
 * 而界面上会显示成「已保管」——用户于是以为备份还在跑。
 *
 * 这与「不显示一张没有依据的健康卡」是同一件事：
 * **一个看起来正常、实际什么都没做的东西，比一个明说自己没做的东西危险。**
 *
 * ## 能力的人话名字：查不到就不说
 *
 * `vendor.capability.<kind>` 那十条模板**已经落地**（流 0，2026-08-07，
 * 起因是流 A1 的 `docs/contract-requests/a1-能力的人话名字.md`）。
 * 这里的逻辑照抄流 A1 的 `capabilityword.go`：**按约定 id 去查，
 * 查不到就这一条不说名字**——落地之后这条兜底仍然留着，
 * 它守的是「将来多一个 kind 而模板还没跟上」的那一天。
 *
 * 两条都不能走的路：
 *
 *   在这儿写死「对象存储」「域名注册」  违反 CLAUDE.md §3，用户可见文案必须来自模板
 *   把 `object_storage` 直接显示出来    用户在界面上读到内部枚举值
 *
 * 名字缺席不影响**状态**看得出来——那才是这一块要守的东西。
 * 这个文件在落地那天确实一行都没改，说到做到。
 *
 * **但这段注释原来写着「还没落地」，落地那天它变成了假话，
 * 而没有任何东西会因此红。** 唯一在那一刻红的是那条断言
 * `capabilityWord('dns')` 为 `undefined` 的用例——所以这一族的看门人
 * 是用例，不是注释（CLAUDE.md §8「契约落地那天，回头核一遍所有说
 * 『还没有』的话」）。
 */
export function CapabilityRow({ capability }: { capability: Schemas['VendorCapability'] }) {
  const word = capabilityWord(capability.kind)

  /**
   * **一行说不出话就整行不画。**
   *
   * 十条名字落地之后 `word` 一般都有值，所以这条早退现在很少走到。
   * **它守的是将来多一个 kind 而模板还没跟上的那一天**：那时名字没有，
   * 而 `in_use` / `planned` 这两态本来就没有 `blocked_reason_text`
   * （正常状态不需要解释）。两样都缺时画出来的是一个光秃秃的圆点，
   * 旁边什么都没有——那不是信息，是看着像坏了。
   *
   * 有话可说的那些照画，所以**四态里真正要紧的那两态
   * （`unverified` / `not_enabled`）任何时候都看得见** ——
   * 它们各自都带着后端渲染好的那句解释。
   */
  if (!word && !capability.blocked_reason_text) return null

  return (
    <div className="flex items-start gap-npc-gap-sm">
      <StatusDot tone={statusTone(capability.status)} className="mt-[5px]" />
      <div className="min-w-0">
        {word ? <span className="text-npc-caption text-npc-ink-medium">{word}</span> : null}
        {/* 卡在哪儿，**后端渲染好的人话**（库里存的是模板 id）。
            `in_use` 的那些没有这一句，正常状态不需要解释 */}
        {capability.blocked_reason_text ? (
          <p className="m-0 text-npc-caption leading-[1.6] text-npc-ink-soft">
            {capability.blocked_reason_text}
          </p>
        ) : null}
      </div>
    </div>
  )
}

/**
 * `vendor.capability.<kind>`。**查不到返回 undefined，绝不兜底。**
 *
 * 走 `lookup` 而不是 `render`：`render` 的 id 是编译期联合类型，
 * 而这个 id 是**运行时按 kind 拼出来的**，编译期不知道拼成什么。
 * `lookup` 查不到给 `undefined`，正好就是我们要的行为。
 *
 * （原来这句写的是「十条还不存在，写进联合类型里编不过」。
 * 十条已经存在了，而结论没变——理由换成真正成立的那个。）
 */
export function capabilityWord(kind: Schemas['VendorCapabilityKind']): string | undefined {
  return lookup(`vendor.capability.${kind}`)?.text
}

/**
 * 四态各用什么颜色。
 *
 * **`unverified` 是琥珀不是绿**——它看起来和 `in_use` 一样，
 * 而这正是它危险的地方。给它一个和「能用」相同的颜色，
 * 就等于在界面上把四态又压回了两态。
 */
function statusTone(status: Schemas['VendorCapabilityStatus']): TagTone {
  switch (status) {
    case 'in_use':
      return 'ok'
    case 'unverified':
    case 'not_enabled':
      return 'warn'
    case 'broken':
      return 'danger'
    default:
      return 'neutral'
  }
}
